import { Checkbox, Collapse, FormControlLabel, FormGroup, IconButton, Popover, Tooltip } from "@mui/material";
import * as React from "react";
import "../style.module.scss";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';

import { CheckboxField } from "src/shared";
import { breakpoints, useMediaQuery } from "src/shared/hooks/use-media-query";
import FilterSearchField from "src/shared/ui/components/filter-search-field/filter-search-field";
import { CloseIcon } from "../../../svg";

const FilterItem = ({
  checkboxContainerStyle,
  collapsedInitially = true,
  fieldKey,
  fieldName = '',
  filters,
  onChange,
  placeholder,
  showSearch = true,
  totalData,
}) => {
  const [filteredData, setFilteredData] = React.useState(totalData);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldSelectedValues = filters?.[fieldKey?.toLowerCase()] || [];

  const [valuesCheckedState, setValuesCheckedState] = React.useState('');

  const count = React.useMemo(() => {
    if (fieldKey) {
      const selected = fieldSelectedValues;
      return totalData.filter((cat) => selected?.includes(cat.id)).length;
    } else {
      return fieldSelectedValues?.length || 0;
    }
  }, [fieldKey, fieldSelectedValues, totalData]);

  const [isCollapsed, setIsCollapsed] = React.useState(
    count > 0 ? false : collapsedInitially,
  );

  const handleValuesCheckedState = React.useCallback(
    (data) => {
      const allChecked = filteredData.every((item) => data.includes(item.id));
      const noneChecked = filteredData.every((item) => !data.includes(item.id));
      if (allChecked) {
        setValuesCheckedState('all');
      }
      if (noneChecked) {
        setValuesCheckedState('');
      }
      if (!allChecked && !noneChecked) {
        setValuesCheckedState('indeterminate');
      }
    },
    [filteredData],
  );

  React.useEffect(() => {
    if (count > 0) {
      if (fieldKey) {
        const selected = fieldSelectedValues;
        handleValuesCheckedState([...selected]);
      } else {
        handleValuesCheckedState([...fieldSelectedValues]);
      }
    } else {
      setValuesCheckedState('');
    }
  }, [count, fieldKey, fieldSelectedValues, handleValuesCheckedState]);
  return (
    <div className={"filterItemContainer"}>
      <div>
        <div className={"filterItemCheckbox"}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!valuesCheckedState}
                indeterminate={valuesCheckedState === 'indeterminate'}
                name={fieldKey}
                onChange={() => {
                  if (isCollapsed) {
                    setIsCollapsed(false);
                  }
                  if (
                    valuesCheckedState === 'indeterminate' ||
                    valuesCheckedState === 'all'
                  ) {
                    setValuesCheckedState('');
                    onChange(fieldKey.toLowerCase(), '');
                    return;
                  }
                  setValuesCheckedState('all');
                  if (fieldKey) {
                    onChange(fieldKey.toLowerCase(), [
                      ...totalData.map((item) => item.id),
                      ...fieldSelectedValues,
                    ]);
                  } else {
                    onChange(
                      fieldKey.toLowerCase(),
                      totalData.map((item) => item.id),
                    );
                  }
                }}
              />
            }
          />
          <div
            className={"filterHeaderAction"}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <span>
              {fieldName.replace(/^./, (string_) => string_.toUpperCase())}{' '}
              <span className={"count"}>{`(${
                count > 0 && count != filteredData.length ? count + '/' : ''
              }${totalData.length})`}</span>
            </span>
            <IconButton>
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </div>
        </div>
      </div>
      <Collapse in={!isCollapsed}>
        <div
          style={checkboxContainerStyle}
        >
       
          {showSearch && (
            <FilterSearchField
              fieldName={fieldName}
              placeholder={placeholder}
              setFiltered={setFilteredData}
              totalValues={totalData}
            />
          )}

          {filteredData.map((item) =>
            fieldName.toLowerCase() === 'programs' ? (
              <CheckboxField
                className={"customCheckBox"}
                key={item.id}
                $label={item.value}
                name={fieldName}
                size="small"
                style={{
                  color: item.color,
                }}
                value={item.id}
              />
            ) : (
              <CheckboxField
                checked={fieldSelectedValues?.includes(item.id)}
                fieldKey={fieldKey}
                key={item.id}
                $label={
                  <Tooltip title={item.value}>
                    <span className={"filterLabel"}>
                      {item.value}
                      {item?.data?.nickName ? ` (${item?.data?.nickName})` : ''}
                    </span>
                  </Tooltip>
                }
                onChange={(e) => {
                  const { checked, value } = e.target;
                  if (checked) {
                    onChange(fieldKey.toLowerCase(), [
                      ...fieldSelectedValues,
                      value,
                    ]);
                  } else {
                    onChange(
                      fieldKey.toLowerCase(),
                      fieldSelectedValues.filter((item) => item !== value),
                    );
                  }
                }}
                size="small"
                value={item.id}
              />
            ),
          )}
       </div>
      </Collapse>
    </div>
  );
};

const CoachFilter = ({ categories, coaches, filters, onChange }) => {
  const isMobile = useMediaQuery(
    [breakpoints.mobile, breakpoints.tablet, breakpoints.desktop],
    [true, true, false],
  );
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleToggleFilter = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isFilterVisible = Boolean(anchorEl);

  const tagCategoriesData = React.useMemo(() => {
    const filtered = categories?.filter(
      (item) => !item?.category?.categoryOptions?.length,
    );
    if (!filtered || !filtered?.length) return [];
    return filtered.flatMap((item) => ({
      id: item?.category?.id,
      value: item?.category?.name,
    }));
  }, [categories]);

  const formFieldCategories = React.useMemo(() => {
    return categories?.filter(
      (item) =>
        item?.category?.categoryOptions?.filter(
          (option) => option?.name && option?.id,
        ).length > 0,
    );
  }, [categories]);

  return (
    <div className={"filterContainer"}>
      {isMobile ? (
        <div>
          <IconButton
            className={"filterToggleButton"}
            onClick={handleToggleFilter}
          >
            {isFilterVisible ? <CloseIcon /> : <FilterListIcon />}
          </IconButton>
          <Popover
            anchorEl={anchorEl}
            
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            onClose={handleClose}
            open={isFilterVisible}
            transformOrigin={{
              horizontal: 'left',
              vertical: 'top',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                color: 'black',
                padding: '16px',
              }}
            >
              {coaches?.length > 0 && (
                <FilterItem
                  checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}
                  fieldKey="coaches"
                  fieldName="coaches"
                  filters={filters}
                  onChange={onChange}
                  placeholder="Search Coach"
                  totalData={coaches}
                />
              )}
              {formFieldCategories?.length > 0 &&
                formFieldCategories.map((item) => (
                  <FilterItem
                                      checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}

                    fieldKey="categories"
                    fieldName={item?.category?.name}
                    filters={filters}
                    key={item?.id}
                    onChange={onChange}
                    totalData={item?.category?.categoryOptions.map(
                      (option) => ({
                        id: `${item?.category?.id}_${option.id}`,
                        value: option?.name,
                      }),
                    )}
                  />
                ))}
              <FilterItem
                                  checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}

                fieldKey="tags"
                fieldName="tags"
                filters={filters}
                onChange={onChange}
                placeholder="Search Tags"
                totalData={tagCategoriesData}
              />
            </div>
          </Popover>
        </div>
      ) : (
        <div
          style={{ backgroundColor: 'white', color: 'black', padding: 0 }}
        >
          {coaches?.length > 0 && (
            <FilterItem
                                checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}

              fieldKey="coaches"
              fieldName="coaches"
              filters={filters}
              onChange={onChange}
              placeholder="Search Coach"
              totalData={coaches}
            />
          )}
          {formFieldCategories?.length > 0 &&
            formFieldCategories.map((item) => (
              <FilterItem
                                  checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}

                fieldKey="categories"
                fieldName={item?.category?.name}
                filters={filters}
                key={item?.id}
                onChange={onChange}
                totalData={item?.category?.categoryOptions.map((option) => ({
                  id: `${item?.category?.id}_${option.id}`,
                  value: option?.name,
                }))}
              />
            ))}
          <FilterItem
                              checkboxContainerStyle={{ padding: '8px',maxHeight:'300px', overflow:'scroll' }}

            fieldKey="tags"
            fieldName="tags"
            filters={filters}
            onChange={onChange}
            placeholder="Search Tags"
            totalData={tagCategoriesData}
          />
        </div>
      )}
    </div>
  );
};

export default CoachFilter;
